import {
  CartItem as CartItemComponent,
  CartSummary,
  ShippingAddressStep,
} from "@/components/products";
import {
  AddressSelector,
  type ProfileAddress,
} from "@/components/products/address-selector";
import {
  computeOrderConditionStatus,
  type SelectionRuleCondition,
} from "@/components/products/conditions-dialog";
import { ConditionsSummary } from "@/components/products/conditions-summary";
import { extractConditionCriteria } from "@/lib/conditions";
import {
  ContactInfoSection,
  type ContactInfo,
} from "@/components/products/contact-info-section";
import {
  isAddressComplete,
  normalizeCountryCode,
  type MemberAddress,
} from "@/lib/queries/member-data";
import {
  calculateCartQuantity,
  exceedsMaxPrice,
  exceedsMaxQuantity,
  meetsMinQuantity,
  type CartItem,
} from "@/lib/queries/products";
import {
  Button,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
} from "@repo/ui";
import { cn } from "@repo/ui/lib/utils";
import { AlertCircle, ChevronDown, ShoppingCart } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";

interface SelectionRule {
  minQuantity?: number | null;
  maxQuantity?: number | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  currencyCode?: string | null;
  conditions?: SelectionRuleCondition[] | null;
}

interface CartDialogProps {
  cart: CartItem[];
  selectionRule?: SelectionRule;
  currencyCode: string;
  showPrice?: boolean;
  /** When false, suppresses Vendor badge on cart items. Driven by catalog `hideVendorsFromCreators`. */
  showVendor?: boolean;
  /** When false, suppresses Tag badges on cart items. Driven by catalog `hideTagsFromCreators`. */
  showTags?: boolean;
  memberAddress: MemberAddress | null;
  memberEmail: string | null;
  isSubmitting: boolean;
  onUpdateQuantity: (id: string, quantity: number) => void;
  onRemoveItem: (id: string) => void;
  onAddressChange: (address: MemberAddress) => void;
  onSubmitOrder: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerClassName?: string;
  profileAddresses?: ProfileAddress[];
  contactInfoMode?: "same" | "different";
  onContactInfoModeChange?: (mode: "same" | "different") => void;
  contactInfo?: ContactInfo;
  onContactInfoChange?: (info: ContactInfo) => void;
  /** Whether the selected address has all required contact fields. */
  contactInfoComplete?: boolean;
  /** Inline validation errors from the last submit attempt. */
  submitErrors?: Partial<
    Record<"address" | "contact" | "cart" | "submit", string>
  >;
  /** Validation error from add-to-cart or update-quantity operations. */
  cartValidationError?: string | null;
  /** Brand-level minimum stock level to subtract from available inventory. */
  minimumStockLevel?: number;
}

export function CartDialog({
  cart,
  currencyCode,
  showPrice = true,
  showVendor = true,
  showTags = true,
  selectionRule,
  memberAddress,
  memberEmail,
  isSubmitting,
  onUpdateQuantity,
  onRemoveItem,
  onAddressChange,
  onSubmitOrder,
  open,
  onOpenChange,
  triggerClassName,
  profileAddresses,
  contactInfoMode = "same",
  onContactInfoModeChange,
  contactInfo,
  onContactInfoChange,
  contactInfoComplete = true,
  submitErrors,
  cartValidationError,
  minimumStockLevel = 0,
}: CartDialogProps) {
  const cartQuantity = calculateCartQuantity(cart);
  const addressReady = memberAddress ? isAddressComplete(memberAddress) : false;

  // Determine selected address ID by matching fields
  const selectedAddressId =
    profileAddresses?.find(
      (addr) =>
        addr.firstName === memberAddress?.firstName &&
        addr.lastName === memberAddress?.lastName &&
        addr.line1 === memberAddress?.address1 &&
        (addr.line2 || "") === (memberAddress?.address2 || "") &&
        addr.city === memberAddress?.city &&
        addr.zip === memberAddress?.zip &&
        normalizeCountryCode(addr.country || "") ===
          normalizeCountryCode(memberAddress?.country || ""),
    )?.id ?? "new";

  // Stable reference to conditions array from the prop (avoids creating a new [] each render)
  const conditions = selectionRule?.conditions;

  // Check all conditions (order-level + product-level)
  const productConditionsMet = selectionRule
    ? computeOrderConditionStatus(cart, selectionRule).fulfilled
    : true;

  // Extract all condition-relevant criteria for badge highlighting
  const conditionCriteria = useMemo(
    () => extractConditionCriteria(conditions ?? []),
    [conditions],
  );

  const isValid =
    cart.length > 0 &&
    meetsMinQuantity(cart, selectionRule?.minQuantity) &&
    !exceedsMaxQuantity(cart, selectionRule?.maxQuantity) &&
    !exceedsMaxPrice(cart, selectionRule?.maxPrice) &&
    productConditionsMet &&
    addressReady &&
    (contactInfoMode === "same"
      ? !!memberEmail
      : !!contactInfo?.firstName &&
        !!contactInfo?.lastName &&
        !!contactInfo?.email &&
        !!contactInfo?.phone);

  const [hasScrolled, setHasScrolled] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      setHasScrolled(scrollRef.current.scrollTop > 0);
    }
  }, []);

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("relative min-w-[130px] gap-1.5", triggerClassName)}
        >
          <ShoppingCart className="h-3.5 w-3.5" />
          Cart
          {cartQuantity > 0 && (
            <span className="absolute -top-2 -right-2 bg-teal-600 text-white text-xs font-medium rounded-full h-5 w-5 flex items-center justify-center">
              {cartQuantity}
            </span>
          )}
        </Button>
      </ResponsiveDialogTrigger>
      <ResponsiveDialogContent className="w-full sm:max-w-lg flex flex-col max-h-[90vh] overflow-hidden pb-0 bg-background">
        <ResponsiveDialogHeader className="relative z-[1] pb-10 -mb-10 bg-gradient-to-b from-background from-33% to-transparent">
          <ResponsiveDialogTitle>Your Cart</ResponsiveDialogTitle>
        </ResponsiveDialogHeader>

        {/* EPD-7772: Show validation error even when cart is empty */}
        {cartValidationError && (
          <div
            role="alert"
            aria-live="assertive"
            className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg mb-4"
          >
            <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
            <p className="text-sm text-destructive">{cartValidationError}</p>
          </div>
        )}

        {cart.length === 0 ? (
          <div className="flex-1 flex items-center justify-center min-h-[300px]">
            <div className="text-center py-12">
              <ShoppingCart className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">Your cart is empty</p>
            </div>
          </div>
        ) : (
          <>
            {/* Scrollable area with cart items, summary, conditions, and shipping */}
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto -mx-6 px-6 pb-6 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']"
            >
              {/* Conditions summary at top of cart — only render wrapper when content will show */}
              {selectionRule &&
                ((selectionRule.conditions &&
                  selectionRule.conditions.length > 0) ||
                  selectionRule.minQuantity != null ||
                  selectionRule.maxQuantity != null ||
                  (showPrice && selectionRule.maxPrice != null)) && (
                  <div className="pt-2 pb-4 border-b border-border mb-4">
                    <ConditionsSummary
                      selectionRule={selectionRule}
                      cart={cart}
                      currencyCode={currencyCode}
                      showPrice={showPrice}
                    />
                  </div>
                )}

              <div className="space-y-2">
                {cart.map((item) => {
                  // Shopify variant-level inventory check (subtract minimumStockLevel to match handleUpdateQuantity logic)
                  const effectiveVariantStock =
                    item.inventoryQuantity != null
                      ? Math.max(0, item.inventoryQuantity - minimumStockLevel)
                      : null;
                  const atVariantInventoryLimit =
                    item.tracksInventory === true &&
                    effectiveVariantStock != null &&
                    item.sellWhenOutOfStock !== true &&
                    item.quantity >= effectiveVariantStock;
                  // Catalog-level maxInventoryLimit check (cross-variant: sum all variants of same product)
                  const atCatalogInventoryLimit = (() => {
                    if (!item.maxInventoryLimit) return false;
                    const otherVariantsQty = cart
                      .filter(
                        (other) =>
                          other.catalogProductId != null &&
                          other.catalogProductId === item.catalogProductId &&
                          other.id !== item.id,
                      )
                      .reduce((sum, other) => sum + other.quantity, 0);
                    const remaining =
                      item.maxInventoryLimit -
                      (item.currentOrderedQuantity ?? 0) -
                      otherVariantsQty;
                    return item.quantity >= remaining;
                  })();
                  // Compute max allowed quantity for display
                  const maxAllowedQuantity = (() => {
                    if (!item.maxInventoryLimit) return undefined;
                    const otherVariantsQty = cart
                      .filter(
                        (other) =>
                          other.catalogProductId != null &&
                          other.catalogProductId === item.catalogProductId &&
                          other.id !== item.id,
                      )
                      .reduce((sum, other) => sum + other.quantity, 0);
                    return Math.max(
                      0,
                      item.maxInventoryLimit -
                        (item.currentOrderedQuantity ?? 0) -
                        otherVariantsQty,
                    );
                  })();
                  const atInventoryLimit =
                    atVariantInventoryLimit || atCatalogInventoryLimit;
                  return (
                    <CartItemComponent
                      key={item.id}
                      item={item}
                      currencyCode={currencyCode}
                      showPrice={showPrice}
                      showVendor={showVendor}
                      showTags={showTags}
                      onUpdateQuantity={onUpdateQuantity}
                      onRemove={onRemoveItem}
                      disableIncrement={atInventoryLimit}
                      conditionCriteria={conditionCriteria}
                      maxAllowedQuantity={maxAllowedQuantity}
                    />
                  );
                })}
              </div>

              <div className="border-t border-border pt-4 mt-4 space-y-4 pb-4">
                <CartSummary
                  cart={cart}
                  currencyCode={currencyCode}
                  showPrice={showPrice}
                  selectionRule={selectionRule}
                />
              </div>

              {/* Shipping Address Step */}
              <div className="space-y-4">
                {profileAddresses && profileAddresses.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-sm">Select Address</h4>
                    <AddressSelector
                      profileAddresses={profileAddresses}
                      selectedAddressId={selectedAddressId}
                      onAddressChange={onAddressChange}
                    />
                  </div>
                )}

                <ShippingAddressStep
                  address={memberAddress}
                  onAddressChange={onAddressChange}
                />
                {submitErrors?.address && (
                  <p
                    role="alert"
                    className="text-sm text-destructive flex items-center gap-1.5 mt-2"
                  >
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {submitErrors.address}
                  </p>
                )}
              </div>

              {/* Contact Information */}
              {addressReady &&
                onContactInfoModeChange &&
                onContactInfoChange && (
                  <ContactInfoSection
                    mode={contactInfoMode}
                    onModeChange={onContactInfoModeChange}
                    contactInfo={
                      contactInfo ?? {
                        firstName: "",
                        lastName: "",
                        email: "",
                        phone: "",
                      }
                    }
                    onContactInfoChange={onContactInfoChange}
                    memberAddress={memberAddress}
                    memberEmail={memberEmail}
                    disableSameOption={!contactInfoComplete}
                  />
                )}
              {submitErrors?.contact && (
                <p
                  role="alert"
                  className="text-sm text-destructive flex items-center gap-1.5"
                >
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  {submitErrors.contact}
                </p>
              )}
            </div>
            {/* Fixed submit button at bottom */}
            <div className="relative px-6 pb-4 bg-background space-y-2">
              <div className="pointer-events-none absolute inset-x-0 -top-16 h-16 bg-gradient-to-t from-background to-transparent" />

              {/* Scroll hint — fades out once user scrolls */}
              <div
                className={cn(
                  "flex items-center justify-center gap-1.5 pb-1 text-xs text-muted-foreground/80 transition-opacity duration-500",
                  hasScrolled ? "opacity-0 h-0 overflow-hidden" : "opacity-100",
                )}
              >
                <span>Scroll for more</span>
                <ChevronDown className="h-3 w-3 animate-bounce" />
              </div>

              <Button
                className="w-full"
                onClick={onSubmitOrder}
                disabled={!isValid || isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit Order"}
              </Button>

              {!addressReady && (
                <p className="text-xs text-amber-600 dark:text-amber-400 text-center">
                  Please confirm your shipping address before submitting
                </p>
              )}
              {addressReady && contactInfoMode === "same" && !memberEmail && (
                <p className="text-xs text-amber-600 dark:text-amber-400 text-center">
                  No email on file. Please select &quot;Update contact
                  info&quot; to provide one.
                </p>
              )}
              {submitErrors?.cart && (
                <p
                  role="alert"
                  className="text-sm text-destructive flex items-center justify-center gap-1.5"
                >
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  {submitErrors.cart}
                </p>
              )}
              {submitErrors?.submit && (
                <div
                  role="alert"
                  aria-live="assertive"
                  className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
                >
                  <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                  <p className="text-sm text-destructive">
                    {submitErrors.submit}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
